import { TFunction } from 'i18next';
import { upperFirst, trim } from 'lodash';

import {
  getCountryText,
  formattedDefaultAttributes,
  formattedStringSelection,
  getSelectedAllergen,
  getSelectedCertifiedSafe,
  getSelectedRestriction,
  getSelectedPhysicalState,
  getOthersPhysicalState,
} from '../containers/Product/helper';

interface ProductNode {
  id: number;
  brand: string;
  localName?: string;
  aroma: boolean;
  ingredients?: string;
  public: boolean;
  prototype: boolean;
  dietaryRestrictions?: string;
  restrictions?: string;
  servingVessel?: string;
  physicalState?: string;
  country?: string;
  countryOfPurchase?: string;
  defaultAttributes?: Record<string, unknown>;
  productAttributes?: unknown;
  nutritionalInformation?: unknown;
  folderId?: number;
  hasTextureComponents?: boolean;
  textureComponents?: { defined: boolean; label?: string[] };
  allowCustomTextureComponents?: boolean;
  productCategory?: { id: number; name: string };
  productFeatures?: {
    nodes: Array<{
      id: number;
      productFeatureByProductFeatureId: { id: number; name: string };
    }>;
  };
  productComponentBases?: {
    nodes: Array<{
      id: number;
      productComponentBaseByProductComponentBaseId: {
        id: number;
        name: string;
      };
    }>;
  };
  productComponentOthers?: {
    nodes: Array<{
      id: number;
      productComponentOtherByProductComponentOtherId: {
        id: number;
        name: string;
      };
    }>;
  };
  selectedProductQuestions?: {
    nodes: Array<{
      id: number;
      question: { id: number; slug: string };
    }>;
  };
}

function getFormattedTagObject(label: string, id: number | string) {
  return {
    label: upperFirst(String(label)),
    id: String(id),
  };
}

/** Parses nutritional info from DB format ("25 mg") to form format */
function parseNutritionalInfo(dbInfo: any): Record<string, any> {
  if (!dbInfo) return {};
  
  const result: Record<string, any> = {};
  
  Object.keys(dbInfo).forEach((key) => {
    if (key === 'additional') {
      result.additional = dbInfo[key];
    } else if (key !== '_unit' && !key.includes('_unit')) {
      const value = String(dbInfo[key]);
      const match = value.match(/^(\D?\d+(?:\.\d+)?)(\s?\w+$)?/);
      
      if (match) {
        result[key] = match[1];
        const unit = match[2] && trim(match[2]);
        if (unit) {
          result[`${key}_unit`] = { label: unit, value: unit };
        }
      } else {
        result[key] = value;
      }
    }
  });
  
  return result;
}

/**
 * Builds the clone data from a full product object for the product create form.
 * Excludes name and images — those must be provided fresh by the user.
 */
// eslint-disable-next-line complexity -- pre-existing complexity (29); surfaced by the ESLint 9 flat-config migration (AIPD-1397), refactor tracked separately
export function buildCloneData(product: ProductNode, t: TFunction) {
  const allergenList = t('allergenInfo.contains', {
    returnObjects: true,
  }) as Array<{ value: string; label: string }>;

  const certifiedSafeList = t('allergenInfo.safe', {
    returnObjects: true,
  }) as Array<{ value: string; label: string }>;

  const restrictionListOptions = t('dietaryRestrictions.contains', {
    returnObjects: true,
  }) as Array<{ value: string; label: string }>;

  const parsedNutritionalInfo = parseNutritionalInfo(product.nutritionalInformation);

  const cloneData = {
    brand: product.brand,
    localName: product.localName ?? '',
    aroma: product.aroma,
    ingredients: product.ingredients ?? '',
    public: product.public,
    prototype: product.prototype,
    productAttributes: product.productAttributes,
    productClassAttribute:
      product.defaultAttributes
        ? formattedDefaultAttributes(t, product.defaultAttributes)
        : undefined,
    nutritionalInfo: parsedNutritionalInfo,
    productCategory: product.productCategory
      ? [
          getFormattedTagObject(
            product.productCategory.name,
            product.productCategory.id,
          ),
        ]
      : [],
    productFeature:
      product.productFeatures?.nodes.map(
        ({ productFeatureByProductFeatureId }) =>
          getFormattedTagObject(
            productFeatureByProductFeatureId.name,
            productFeatureByProductFeatureId.id,
          ),
      ) ?? [],
    productComponentBase:
      product.productComponentBases?.nodes.map(
        ({ productComponentBaseByProductComponentBaseId }) =>
          getFormattedTagObject(
            productComponentBaseByProductComponentBaseId.name,
            productComponentBaseByProductComponentBaseId.id,
          ),
      ) ?? [],
    productComponentOther:
      product.productComponentOthers?.nodes.map(
        ({ productComponentOtherByProductComponentOtherId }) =>
          getFormattedTagObject(
            productComponentOtherByProductComponentOtherId.name,
            productComponentOtherByProductComponentOtherId.id,
          ),
      ) ?? [],
    allergens:
      product.dietaryRestrictions
        ? product.dietaryRestrictions
            .split(',')
            .filter((allergen) =>
              allergenList.find((value) => value.value === allergen),
            )
            .map((allergen) => getSelectedAllergen(t, allergen))
            .filter(Boolean)
        : [],
    certifiedSafe:
      product.dietaryRestrictions
        ? product.dietaryRestrictions
            .split(',')
            .filter((allergen) =>
              certifiedSafeList.find((value) => value.value === allergen),
            )
            .map((allergen) => getSelectedCertifiedSafe(t, allergen))
            .filter(Boolean)
        : [],
    restrictions:
      product.restrictions
        ? product.restrictions
            .split(',')
            .filter((restriction) =>
              restrictionListOptions.find(
                (value) => value.value === restriction,
              ),
            )
            .map((restriction) => getSelectedRestriction(t, restriction))
            .filter(Boolean)
        : [],
    servingVessel:
      product.servingVessel
        ? formattedStringSelection(t, product.servingVessel, 'servingVessel')
        : undefined,
    physicalState:
      product.physicalState
        ? product.physicalState
            .split(',')
            .map((state) => getSelectedPhysicalState(t, state))
            .filter(Boolean)
        : [],
    custom_physicalState:
      product.physicalState
        ? getOthersPhysicalState(product.physicalState)
        : null,
    country: product.country
      ? { label: getCountryText(t, product.country), value: product.country }
      : null,
    countryOfPurchase: product.countryOfPurchase
      ? {
          label: getCountryText(t, product.countryOfPurchase),
          value: product.countryOfPurchase,
        }
      : null,
    texture: product.hasTextureComponents,
    definedComponents:
      product.textureComponents && product.textureComponents.defined
        ? 'true'
        : 'false',
    definedComponentNames:
      product.textureComponents?.defined && product.textureComponents?.label
        ? {
            component: product.textureComponents.label.map((name) => ({
              key: null,
              value: name,
            })),
          }
        : null,
    undefinedComponentTotal:
      product.textureComponents &&
      !product.textureComponents.defined &&
      product.textureComponents.label
        ? {
            label: product.textureComponents.label.length,
            value: product.textureComponents.label.length,
          }
        : { label: 1, value: 1 },
    allowCustomTextureComponents: product.allowCustomTextureComponents,
    folder: product.folderId,
    questions: product.selectedProductQuestions
      ? product.selectedProductQuestions.nodes.map((node) => node.question.id)
      : [],
  };
  
  return cloneData;
}
